import { useState } from "react";
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";

function flowArrow(inVal, outVal) {
    if (inVal > outVal * 1.2) return "north";
    if (outVal > inVal * 1.2) return "south";
    return "remove"; // neutral
}

function heatColor(value, max) {
    if (max === 0) return "transparent";
    const t = Math.min(1, value / max);

    // Soft UI style red → orange → yellow
    const r = 255;
    const g = Math.floor(200 - 120 * t);
    const b = Math.floor(200 - 160 * t);

    return `rgba(${r},${g},${b},0.15)`;
}

/* Find index closest to epoch */
function indexForEpoch(series, epoch) {
    if (!series?.timestamps?.length) return 0;
    let i = series.timestamps.findIndex(t => t >= epoch);
    return i === -1 ? series.timestamps.length - 1 : i;
}

/* Recursive stat aggregation */
function computeStats(node, statsByUuid, epoch) {
    if (node.uuid) {
        const s = statsByUuid[node.uuid];
        if (!s) return { occupancy: 0, ingress: 0, egress: 0 };

        const i = indexForEpoch(s, epoch);
        return {
            occupancy: s.occupancy[i] || 0,
            ingress: s.ingress[i] || 0,
            egress: s.egress[i] || 0
        };
    }

    let totals = { occupancy: 0, ingress: 0, egress: 0 };

    Object.values(node.children || {}).forEach(child => {
        const v = computeStats(child, statsByUuid, epoch);
        totals.occupancy += v.occupancy;
        totals.ingress += v.ingress;
        totals.egress += v.egress;
    });

    return totals;
}

function TreeRow({ node, level, statsByUuid, epoch, stationTotal }) {
    const [open, setOpen] = useState(true);
    const hasChildren = !!node.children;

    const stats = computeStats(node, statsByUuid, epoch);
    const pct = stationTotal ? (stats.occupancy / stationTotal) * 100 : 0;
    const arrow = flowArrow(stats.ingress, stats.egress);


    return (
        <>
            <TableRow
                hover
                sx={{
                    backgroundColor: heatColor(stats.occupancy, stationTotal)
                }}
            >
                <TableCell>
                    <SoftBox display="flex" alignItems="center" pl={level * 2}>
                        {hasChildren && (
                            <Icon
                                onClick={() => setOpen(!open)}
                                sx={{ cursor: "pointer", mr: 1, fontSize: 16 }}
                            >
                                {open ? "expand_more" : "chevron_right"}
                            </Icon>
                        )}

                        <SoftTypography fontWeight={hasChildren ? "bold" : "regular"}>
                            {node.label}
                        </SoftTypography>
                    </SoftBox>
                </TableCell>
                <TableCell align="right">
                    <SoftTypography fontWeight="bold" color="info">
                        {stats.occupancy}
                    </SoftTypography>
                </TableCell>

                <TableCell align="right">
                    <SoftTypography color="success">+{stats.ingress}</SoftTypography>
                </TableCell>

                <TableCell align="right">
                    <SoftTypography color="error">−{stats.egress}</SoftTypography>
                </TableCell>

                <TableCell align="center">
                    <Icon
                        sx={{
                            color:
                                arrow === "north"
                                    ? "#2ecc71"
                                    : arrow === "south"
                                        ? "#e74c3c"
                                        : "#bbb"
                        }}
                    >
                        {arrow}
                    </Icon>
                </TableCell>
            </TableRow>

            {open &&
                hasChildren &&
                Object.entries(node.children).map(([key, child]) => (
                    <TreeRow
                        key={key}
                        node={child}
                        level={level + 1}
                        statsByUuid={statsByUuid}
                        epoch={epoch}
                    />

                ))}
        </>
    );
}

export default function StationTreeTable({ tree, statsByUuid, epoch }) {

    const stationTotals = computeStats(tree, statsByUuid, epoch);


    return (
        <Card>
            <SoftBox p={3}>
                <SoftTypography variant="h6" mb={2}>
                    Live Station Breakdown
                </SoftTypography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Area / Camera</TableCell>
                            <TableCell align="right">Occupancy</TableCell>
                            <TableCell align="right">In</TableCell>
                            <TableCell align="right">Out</TableCell>
                            <TableCell align="center">Flow</TableCell>

                        </TableRow>
                    </TableHead>

                    <TableBody>
                        <TreeRow
                            node={tree}
                            level={0}
                            statsByUuid={statsByUuid}
                            epoch={epoch}
                            stationTotal={stationTotals.occupancy}
                        />
                    </TableBody>
                </Table>
            </SoftBox>
        </Card>
    );
}

